import React, { useCallback, useMemo } from 'react';
import { EntityIdentificationType } from '@ydistri/api-sdk';
import { useAppDispatch } from '../../../../../store';
import {
  setImportedEntityListItems,
  setProductItemsContent,
} from '../../entityListAdministrationSlice';
import {
  EntityListContentItem,
  EntityListProductInputData,
  EntityListTypeSupported,
  getEntityListSpecification,
} from '../../entityListsLib';
import { useCurrentEntityListState } from '../../hooks/useCurrentEntityListState';
import ListItemsTextArea from '../../../../CalculationDetail/Redistribution/parts/import/components/common/ListItemsTextArea';
import { ImportedItemRow } from '../../../../../lib/imports/importTypes';
import {
  getPlainTextFromStructuredData,
  selectImportableItemParser,
} from '../../../../../lib/imports/importLib';
import useEntityListImportFormat from '../../hooks/useEntityListImportFormat';
import useImportedItemValidatorContext from '../../hooks/useImportedItemValidatorContext';

interface EntityListItemsAreaProps {
  entityListType: EntityListTypeSupported;
  id?: string;
  label?: string;
  disabled?: boolean;
}

const EntityListItemsArea: React.FC<EntityListItemsAreaProps> = ({
  entityListType,
  label,
  id,
  disabled = false,
}) => {
  const specification = getEntityListSpecification(entityListType);
  const entityListState = useCurrentEntityListState(entityListType);

  const warehouseIdentificationType =
    entityListState?.storeIdentificationType ?? EntityIdentificationType.CustomerId;
  const productIdentificationType =
    entityListState?.productIdentificationType ?? EntityIdentificationType.CustomerId;
  const productItemsContent = entityListState?.productItemsContent ?? '';

  const dispatch = useAppDispatch();

  const validatorContext = useImportedItemValidatorContext(
    productIdentificationType,
    warehouseIdentificationType,
  );

  const possiblePlaceholders: Record<EntityListContentItem, string> = useMemo(
    () => ({
      [EntityListContentItem.PRODUCT]: `product ${productIdentificationType === EntityIdentificationType.Code ? 'code' : 'ID'}`,
      [EntityListContentItem.STORE]: `store ${warehouseIdentificationType === EntityIdentificationType.Code ? 'code' : 'ID'}`,
      [EntityListContentItem.QUANTITY]: 'quantity',
    }),
    [productIdentificationType, warehouseIdentificationType],
  );

  const itemsPlaceholder = useMemo(() => {
    const placeholderArray = specification
      .getContentTableStructure()
      .map(contentItem => possiblePlaceholders[contentItem]);
    const lastItem = placeholderArray.pop();
    return `Paste ${
      placeholderArray.join(', ') +
      (lastItem ? ` ${placeholderArray.length > 0 ? ' and ' : ''}${lastItem}` : '')
    } from Excel ${placeholderArray.length > 0 ? `(or separated by semicolons)` : ''}`;
  }, [specification, possiblePlaceholders]);

  const inputFormat = useEntityListImportFormat(entityListType);

  /**
   * Saves the content of the text area to state
   * Called on text area's change event
   */
  const entityListItemsHandler = useCallback(
    (newValue: string, rows?: readonly ImportedItemRow<EntityListProductInputData>[]) => {
      dispatch(setProductItemsContent({ entityListType, data: newValue }));

      if (rows) {
        dispatch(setImportedEntityListItems({ entityListType, data: [...rows] }));
      }
    },
    [dispatch, entityListType],
  );

  const serializer = useCallback(
    (data: readonly ImportedItemRow<EntityListProductInputData>[]) => {
      return getPlainTextFromStructuredData(data, inputFormat, ',');
    },
    [inputFormat],
  );

  return (
    <ListItemsTextArea<EntityListProductInputData>
      label={label}
      id={id ?? 'EntityListItemsInput'}
      value={productItemsContent}
      placeholder={itemsPlaceholder}
      onValueChanged={entityListItemsHandler}
      parserProvider={selectImportableItemParser}
      serializer={serializer}
      inputFormat={inputFormat}
      validators={specification.getValidators(validatorContext)}
      disabled={disabled}
    />
  );
};

export default EntityListItemsArea;
